import React, { useRef, useEffect, useState } from 'react';
import FloatingAchievement from './FloatingAchievement';

function ClickButton({
    clickCombo,
    currentCps,
    isJiggling,
    tierBurst,
    setIsJiggling,
    setTierBurst,
    handleIncrement,
    isLoading,
    isOnline,
    comboStatus,
    lastClickTime,
    playAchievementSound,
    setShowGoToLeaderboard
}) {
    const measureRef = useRef(null);
    const [buttonMinHeight, setButtonMinHeight] = React.useState(null);
    const [inputMethod, setInputMethod] = useState(null); // 'pointer' or 'keyboard'
    const [showWarning, setShowWarning] = useState(false);
    const [isPressed, setIsPressed] = useState(false);
    const [floaters, setFloaters] = useState([]);
    const [ripples, setRipples] = useState([]);
    const lastActivityTime = useRef(Date.now());
    const warningTimeoutRef = useRef(null);
    const floaterIdRef = useRef(0);
    const rippleIdRef = useRef(0);
    const isKeyPressed = useRef(false);
    const [progressBar, setProgressBar] = useState(0); // Progress bar state 0-100
    const comboClicksRef = useRef(0); // Track clicks in current combo for progress bar
    const [accumulatedFires, setAccumulatedFires] = useState(0); // Fire emojis earned
    const [showKeepGoing, setShowKeepGoing] = useState(false); // "KEEP GOING!" message
    const [tipDismissed, setTipDismissed] = useState(() => {
        // Check if tip was dismissed in this session
        return sessionStorage.getItem('soundTipDismissed') === 'true';
    }); // Track if sound tip has been dismissed

    // Session tracking for achievements
    const [achievements, setAchievements] = useState([]);
    const [isTextFaded, setIsTextFaded] = useState(false);
    const [lastBurst, setLastBurst] = useState(null); // Store last click burst stats
    const [isDesktop, setIsDesktop] = useState(false);
    const [sessionStats, setSessionStats] = useState({
        sessionId: null,
        maxCombo: 0,
        highestCps: 0
    });
    const achievementIdRef = useRef(0);
    const fadeTimeoutRef = useRef(null);

    // Detect if user is on desktop
    useEffect(() => {
        const checkDesktop = () => {
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            const isMobile = /android|ipad|iphone|ipod|windows phone/i.test(userAgent);
            setIsDesktop(!isMobile);
        };
        checkDesktop();
    }, []);

    // Measure max-state button height (Combo + CPS visible) and set as constant min-height
    useEffect(() => {
        if (measureRef.current && !buttonMinHeight) {
            const h = measureRef.current.offsetHeight;
            if (h && h > 0) {
                setButtonMinHeight(h);
            }
        }
    }, [buttonMinHeight, clickCombo, currentCps]);

    // Haptic feedback for tier bursts
    useEffect(() => {
        if (tierBurst && navigator.vibrate) {
            navigator.vibrate([15, 30, 15]); // Double pulse
        }
    }, [tierBurst]);

    // Reset input method after 500ms of inactivity (same as combo timeout)
    useEffect(() => {
        const checkInactivity = setInterval(() => {
            if (Date.now() - lastActivityTime.current > 500) {
                setInputMethod(null);
            }
        }, 100); // Check every 100ms for quick reset
        return () => clearInterval(checkInactivity);
    }, []);

    // Track session stats and reset progress bar on debounce
    useEffect(() => {
        if (clickCombo > 0) {
            // Update session stats
            setSessionStats(prev => ({
                ...prev,
                sessionId: prev.sessionId || Date.now(),
                maxCombo: Math.max(prev.maxCombo, clickCombo),
                highestCps: Math.max(prev.highestCps, currentCps)
            }));
        } else if (sessionStats.sessionId && sessionStats.maxCombo > 1) {
            // Session ended - reset progress bar and spawn achievement
            setProgressBar(0);
            comboClicksRef.current = 0;
            setAccumulatedFires(0);

            // Session ended - spawn achievement (only for combos of 2 or more)
            const buttonElement = document.querySelector('.increment-button');
            if (buttonElement) {
                // Simply use 50% positioning since button is centered
                const newAchievement = {
                    id: achievementIdRef.current++,
                    combo: sessionStats.maxCombo,
                    highestCps: sessionStats.highestCps,
                    // Will use CSS positioning instead
                };
                setAchievements(prev => [...prev, newAchievement]);

                // Play achievement sound
                if (playAchievementSound) {
                    playAchievementSound();
                }

                // Fade text for 1 second (unfade before animation fully ends)
                setIsTextFaded(true);
                if (fadeTimeoutRef.current) {
                    clearTimeout(fadeTimeoutRef.current);
                }
                fadeTimeoutRef.current = setTimeout(() => {
                    setIsTextFaded(false);
                }, 1000);
            }

            // Save last burst stats
            setLastBurst({
                combo: sessionStats.maxCombo,
                cps: sessionStats.highestCps
            });

            // Show Go to Leaderboard button when debounce happens
            if (setShowGoToLeaderboard) {
                setShowGoToLeaderboard(true);
            }

            // Reset session stats
            setSessionStats({
                sessionId: null,
                maxCombo: 0,
                highestCps: 0
            });
        }
    }, [clickCombo, currentCps, sessionStats.sessionId, sessionStats.maxCombo]);

    // Handle achievement cleanup
    const handleAchievementComplete = (id) => {
        setAchievements(prev => prev.filter(a => a.id !== id));
    };


    const handleDismissTip = () => {
        setTipDismissed(true);
        sessionStorage.setItem('soundTipDismissed', 'true');
    };

    const handleClick = (event, method) => {
        const now = Date.now();

        // Update progress bar (combo-based)
        comboClicksRef.current += 1;
        const newProgress = comboClicksRef.current % 100;

        if (newProgress === 0) {
            // Hit 100! Show "KEEP GOING!" and add a fire
            setProgressBar(100);
            setShowKeepGoing(true);
            setAccumulatedFires(prev => prev + 1);

            // After 1 second, reset bar and hide message
            setTimeout(() => {
                setShowKeepGoing(false);
                setProgressBar(prev => prev === 100 ? 0 : prev);
            }, 1000);
        } else {
            setProgressBar(newProgress);
        }

        // Clear text fade when player clicks
        if (isTextFaded) {
            setIsTextFaded(false);
            if (fadeTimeoutRef.current) {
                clearTimeout(fadeTimeoutRef.current);
            }
        }

        // Clear achievements when player clicks
        if (achievements.length > 0) {
            setAchievements([]);
        }

        // Trigger press animation
        setIsPressed(true);
        setTimeout(() => setIsPressed(false), 150);

        // Haptic feedback for click (short, sharp)
        if (navigator.vibrate) {
            navigator.vibrate(5);
        }

        // Get click position relative to button
        const rect = event.currentTarget.getBoundingClientRect();
        let clickX, clickY;

        if (method === 'pointer' && event.nativeEvent) {
            // For pointer events, use the actual click position
            clickX = event.clientX - rect.left;
            clickY = event.clientY - rect.top;
        } else {
            // For keyboard events, use center of button
            clickX = rect.width / 2;
            clickY = rect.height / 2;
        }

        // Spawn Ripple
        const newRipple = {
            id: rippleIdRef.current++,
            x: clickX,
            y: clickY
        };
        setRipples(prev => [...prev, newRipple]);
        // Auto-remove fallback (though animationEnd handles it)
        setTimeout(() => {
            setRipples(prev => prev.filter(r => r.id !== newRipple.id));
        }, 600);

        // Add random offset around click position (±20px)
        const randomOffsetX = (Math.random() - 0.5) * 40;
        const randomOffsetY = (Math.random() - 0.5) * 40;

        // Only spawn floating number when combo > 2
        if (clickCombo > 2) {
            const newFloater = {
                id: floaterIdRef.current++,
                value: `x${clickCombo}`,
                x: clickX + randomOffsetX,
                y: clickY + randomOffsetY,
            };
            setFloaters(prev => [...prev, newFloater]);

            // Remove floater after animation
            setTimeout(() => {
                setFloaters(prev => prev.filter(f => f.id !== newFloater.id));
            }, 1000);
        }

        // Two-handing prevention disabled - allow both keyboard and mouse
        // to be fair since mobile users can use multiple fingers
        handleIncrement(event);
        lastActivityTime.current = now;
    };

    const getCpsColor = (cps) => {
        if (cps >= 7) return 'cps-red';
        if (cps >= 6) return 'cps-orange';
        if (cps >= 5) return 'cps-yellow';
        if (cps >= 4) return 'cps-yellow';
        return 'cps-white';
    };


    const renderButtonText = () => {
        if (clickCombo <= 1) {
            return (
                <>
                    Click Me! 
                    {isDesktop && <span className="spacebar-hint">(Or Hit Spacebar)</span>}
                    <span role="img" aria-label="plus">➕</span>
                </>
            );
        }

        // For combo >= 2, show "Combo x[number]!"
        if (clickCombo >= 2) {
            return (
                <>
                    <span className="combo-text">Combo</span>
                    {`x${clickCombo}!`}
                    <div
                        className={`cps-block ${currentCps > 0 ? '' : 'cps-hidden'}`}
                        aria-hidden={!(currentCps > 0)}
                    >
                        <div className="count-sep" role="separator" aria-hidden="true" />
                        <span className="cps-text">Clicks/sec (CPS)</span>
                        <span className={getCpsColor(currentCps)}>{currentCps.toFixed(1)}</span>
                    </div>
                </>
            );
        }
    };

    // Prevent scrolling and zooming on touch (only when actively playing)
    const handleTouchStart = (e) => {
        // Only prevent scrolling if combo is active (user is playing)
        if (clickCombo > 2) {
            e.preventDefault();
        }
    };

    const handleTouchMove = (e) => {
        // Only prevent scrolling if combo is active
        if (clickCombo > 2) {
            e.preventDefault();
        }
    };

    return (
        <>
            {/* Progress bar space - outside button container */}
            <div className="progress-bar-wrapper" style={{ minHeight: '76px' }}>
                {/* Always reserve space for accumulated fires - organized in rows of 10 */}
                <div className="accumulated-fires" style={{ minHeight: '38px', marginBottom: '8px' }}>
                    {clickCombo >= 2 && accumulatedFires > 0 && (() => {
                        const fires = [];
                        const rows = Math.ceil(accumulatedFires / 10);

                        for (let row = 0; row < rows; row++) {
                            const startIdx = row * 10;
                            const endIdx = Math.min(startIdx + 10, accumulatedFires);
                            const rowFires = [];

                            for (let i = startIdx; i < endIdx; i++) {
                                rowFires.push(
                                    <span key={`fire-${row}-${i}`} className="accumulated-fire">🔥</span>
                                );
                            }

                            fires.push(
                                <div key={`row-${row}`} className="fire-row">
                                    {rowFires}
                                </div>
                            );
                        }

                        return fires;
                    })()}
                </div>

                {/* Progress bar - show when combo >= 2 */}
                {clickCombo >= 2 ? (
                    <div className="progress-bar-container">
                        <div className="progress-bar-outline">
                            <div className={`progress-bar-fill ${progressBar <= 5 ? 'no-transition' : ''}`} style={{ width: `${progressBar}%` }}>
                                {/* Fire emoji indicators inside bar */}
                                <div className="progress-bar-icons">
                                    {[...Array(Math.floor(progressBar / 10))].map((_, i) => (
                                        <span key={i} className="progress-icon">🔥</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {showKeepGoing && (
                            <div className="progress-bar-label">
                                <span className="keep-going-text">KEEP GOING!</span>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Show sound tip when progress bar is not visible and tip hasn't been dismissed */
                    !tipDismissed ? (
                        <div className="sound-tip-container">
                            <div className="sound-tip">
                                <span className="sound-tip-text">
                                    Play with sound on for the best experience! 🎧
                                </span>
                                <button
                                    className="sound-tip-dismiss"
                                    onClick={handleDismissTip}
                                    aria-label="Dismiss tip"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Invisible placeholder to reserve space */
                        <div className="progress-bar-placeholder" />
                    )
                )}
            </div>

            {/* Button container - separate from progress bar */}
            <div className="button-container" style={{ position: 'relative' }}>
                {/* Hidden measurement element to compute max content height */}
                <div ref={measureRef} className="increment-button measure" aria-hidden="true" style={{ ['--increment-button-height']: 'auto' }}>
                    <div className="button-text">
                        <span className="combo-text">Combo</span>
                        {`x999!`}
                        <div className="count-sep" role="separator" aria-hidden="true" />
                        <span className="cps-text">Clicks/sec (CPS)</span>
                        <span>9.9</span>
                    </div>
                </div>
                <button
                    className={`increment-button ${isJiggling ? 'jiggle' : ''} ${tierBurst ? 'tier-burst-jiggle' : ''} ${isPressed ? 'button-pressed' : ''}`}
                    style={{
                        ['--increment-button-height']: `${Math.max((buttonMinHeight || 0) + 24, 200)}px`,
                        touchAction: clickCombo > 2 ? 'none' : 'manipulation',
                        position: 'relative'
                    }}
                    onPointerDown={(e) => {
                        // Only handle actual pointer events, not keyboard-triggered ones
                        if (e.pointerType !== '') {
                            handleClick(e, 'pointer');
                        }
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onKeyDown={(e) => {
                        // Only allow spacebar, not Enter (to prevent two-handed play)
                        if (e.key === ' ') {
                            e.preventDefault();
                            // Only register the click on initial press, not on repeat
                            if (!isKeyPressed.current) {
                                isKeyPressed.current = true;
                                handleClick(e, 'keyboard');
                            }
                        }
                    }}
                    onKeyUp={(e) => {
                        // Reset the key pressed state when key is released
                        if (e.key === ' ') {
                            isKeyPressed.current = false;
                        }
                    }}
                    onContextMenu={(e) => e.preventDefault()}
                    onAnimationEnd={() => {
                        setIsJiggling(false);
                        setTierBurst(false);
                    }}
                    disabled={isLoading || !isOnline}
                >
                    <div className={`button-text ${isTextFaded ? 'text-faded' : ''}`}>
                        {renderButtonText()}
                        {/* Show last burst stats when button shows "Click Me!" */}
                        {clickCombo <= 1 && lastBurst && (
                            <div className="last-burst-text">
                                <div>Last Click Burst:</div>
                                <div>Combo x{lastBurst.combo}, CPS {lastBurst.cps.toFixed(1)}/sec</div>
                            </div>
                        )}
                    </div>
                    {/* Debounce timer — key restarts CSS animation on every click */}
                    {clickCombo > 1 && (
                        <div
                            key={lastClickTime}
                            className="debounce-timer"
                            role="timer"
                            aria-label="Combo timer"
                        />
                    )}
                    {comboStatus?.showTopLightning && <div className="lightning-top"></div>}
                    {comboStatus?.showBottomLightning && <div className="lightning-bottom"></div>}

                    {/* Floating numbers */}
                    {floaters.map(floater => (
                        <div
                            key={floater.id}
                            className="floating-number"
                            style={{
                                left: `${floater.x}px`,
                                top: `${floater.y}px`,
                                '--combo-color': clickCombo > 60 ? '#ff4444' :
                                    clickCombo > 30 ? '#ff8c00' :
                                        clickCombo > 15 ? '#ffd700' : '#ffffff'
                            }}
                        >
                            {floater.value}
                        </div>
                    ))}

                    {/* Ripples */}
                    {ripples.map(ripple => (
                        <div
                            key={ripple.id}
                            className="click-ripple"
                            style={{
                                left: `${ripple.x}px`,
                                top: `${ripple.y}px`
                            }}
                            onAnimationEnd={() => setRipples(prev => prev.filter(r => r.id !== ripple.id))}
                        />
                    ))}
                </button>
                {/* Two-hand warning disabled since we now allow both input methods
                {showWarning && (
                    <div className="two-hand-warning">
                        🚫 No two-handing! Use {inputMethod === 'pointer' ? 'mouse/touch' : 'spacebar'} only!
                    </div>
                )}
                */}

                {/* Floating achievements */}
                {achievements.map(achievement => (
                    <FloatingAchievement
                        key={achievement.id}
                        id={achievement.id}
                        combo={achievement.combo}
                        highestCps={achievement.highestCps}
                        onComplete={handleAchievementComplete}
                    />
                ))}
            </div>
        </>
    );
}

export default React.memo(ClickButton);